import { IBaseConfigsLoaderTypes } from "../types/IBaseConfigsLoader.types";
import {
    IBaseConfig,
    IBaseGameConfig,
    IGameConfig,
    IGroupConfig,
    IMapQueryConfig,
    IMapQueryItem, TypeAlternativePartners,
    TypePlatform,
} from "../types/IGameLoader.types";
import { IConfigs } from "../types/IConfigs.types";
import { loadXML, mergeObject } from "../utils";
import { parseFiltersConfigs } from "../../utils/parseFiltersConfigs";
import { parseMapQueryParams } from "../../utils/parseMapQueryParams";
import { parseLangsConfigs } from "../../utils/parseLangsConfigs";
import { resolve } from "../../utils/resolve";
import {parseAltPartnersConfig} from "../../utils/parseAltPartnersConfig";

export class BaseConfigsLoader implements IBaseConfigsLoaderTypes {
    configDocument: Document | undefined;
    filters: Map<string, IBaseConfig> = new Map<string, IBaseConfig>();
    langs: Set<string> = new Set();
    baseGameConfig: Partial<IGameConfig> | undefined;
    mapQueryParams: { [index: string]: IMapQueryItem } = {};
    altPartners: TypeAlternativePartners = {};

    constructor(public configs: IConfigs, public platform: TypePlatform) {}

    async load(revision: string): Promise<void> {
        this.configDocument = await loadXML(
            `${this.configs.getPrefixXmlFolder()}/base.xml?_=${revision}`
        );
    }

    prepareAltPartnersConfig(): void {
        if (!this.configDocument) {
            throw new Error(`base.xml as document not found!`);
        }

        this.altPartners = parseAltPartnersConfig(this.configDocument);
    }

    prepare(gameName: string): {
        filter: string[];
        langs: Set<string>;
        config: Partial<IGameConfig>;
        mapQuery: IMapQueryConfig;
    } {
        if (!this.configDocument) {
            throw new Error(`base.xml as document not found!`);
        }

        this.filters = parseFiltersConfigs(this.configDocument);
        this.langs = parseLangsConfigs(this.configDocument);

        const result = this.getGameConfig(gameName);
        if (!result) {
            throw new Error(
                `Sorry, this game "${gameName}" is currently unavailable.\nPlease try another one from the list.`
            );
        }

        this.baseGameConfig = result?.config;

        if (!this.baseGameConfig) {
            throw new Error("Game not found in the base configuration");
        }

        this.mapQueryParams = parseMapQueryParams(this.configDocument);

        return {
            filter: result.filter,
            config: result.config,
            langs: this.langs,
            mapQuery: this.mapQueryParams,
        };
    }

    protected getGameConfig(
        gameName: string
    ): { filter: string[]; config: Partial<IBaseGameConfig> } | undefined {
        if (!this.configDocument) {
            throw new Error(`base.xml as document not found!`);
        }

        const platformFilter = this.platform === "mob" ? "html5" : "html5d";

        const gamesGroup = Array.from(
            this.configDocument.querySelectorAll("config > games")
        );

        for (let i = 0; i < gamesGroup.length; i++) {
            const gameGroupFilter: string[] = resolve<string, string[]>(
                gamesGroup[i].getAttribute("filter") || "",
                (e) => e?.trim().split(/\s+/),
                []
            );
            const result = this.findGamesGroup(gamesGroup[i], gameName);

            if (result) {
                const filter: string[] = [
                    platformFilter,
                    ...gameGroupFilter,
                    ...(result.group.filter || []),
                ]
                    // unique filter list
                    .reduce(
                        (list: string[], el) =>
                            list.includes(el) ? list : [...list, el],
                        []
                    );

                const mergedFilters = filter.reduce(
                    (object: Partial<IBaseConfig>, key: string) =>
                        mergeObject<Partial<IBaseConfig>>(
                            object,
                            this.filters.get(key) || {}
                        ),
                    {}
                );

                // merge by priority
                return {
                    filter,
                    config: mergeObject(
                        mergeObject(mergedFilters, result.group),
                        result.game
                    ),
                };
            }
        }

        return undefined;
    }

    protected findGamesGroup(
        gameContainer: Element,
        gameName: string
    ): { game: IGameConfig; group: IGroupConfig } | undefined {
        const groups = Array.from(gameContainer.children);

        for (let i = 0; i < groups.length; i++) {
            const group = groups[i];

            const filter = resolve(
                group.getAttribute("filter"),
                (e) => e?.trim().split(/\s+/),
                []
            );
            const baseUrl = resolve(
                group.getAttribute("baseUrl"),
                (e) => e?.trim(),
                undefined
            );

            const game = this.findGamesGroupGame(group, gameName);
            if (game) {
                return {
                    game,
                    group: {
                        filter,
                        baseUrl,
                    },
                };
            }
        }

        return undefined;
    }

    protected findGamesGroupGame(
        groupDocument: Element,
        gameName: string
    ): IGameConfig | undefined {
        const gamesElements = Array.from(groupDocument.children);
        for (let i = 0; i < gamesElements.length; i++) {
            const game = gamesElements[i];

            if (game?.textContent?.trim() === gameName) {
                const altGameName = resolve(
                    game.getAttribute("gameName"),
                    (e) => e?.trim(),
                    ""
                );
                const baseUrl = resolve(
                    game.getAttribute("baseUrl"),
                    (e) => e?.trim(),
                    ""
                );

                return {
                    baseUrl,
                    gameName: altGameName || gameName,
                };
            }
        }

        return undefined;
    }
}
